import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

const defaultImages: string[] = [
  "/mining/DSC00193.JPG", "/mining/DSC00194.JPG", "/mining/DSC00195.JPG", "/mining/DSC00196.JPG",
  "/mining/DSC00197.JPG", "/mining/DSC00198.JPG", "/mining/DSC00199.JPG", "/mining/DSC00200.JPG",
  "/mining/DSC00201.JPG", "/mining/DSC00202.JPG", "/mining/DSC00203.JPG", "/mining/DSC00204.JPG",
  "/mining/DSC00205.JPG", "/mining/DSC00206.JPG", "/mining/DSC00207.JPG", "/mining/DSC00208.JPG",
  "/mining/DSC00209.JPG", "/mining/DSC00210.JPG", "/mining/DSC00211.JPG", "/mining/DSC00212.JPG",
  "/mining/DSC00213.JPG", "/mining/DSC00214.JPG", "/mining/DSC00215.JPG", "/mining/DSC00216.JPG",
  "/mining/DSC00217.JPG", "/mining/DSC00218.JPG", "/mining/DSC00219.JPG", "/mining/DSC00220.JPG",
  "/mining/DSC00221.JPG", "/mining/DSC00222.JPG", "/mining/DSC00223.JPG", "/mining/DSC00224.JPG",
  "/mining/DSC00225.JPG", "/mining/DSC00226.JPG", "/mining/DSC00227.JPG", "/mining/DSC00232.JPG",
  "/mining/DSC00228.JPG", "/mining/DSC00229.JPG", "/mining/DSC00230.JPG", "/mining/DSC00231.JPG",
  "/mining/DSC00233.JPG", "/mining/DSC00234.JPG", "/mining/DSC00235.JPG", "/mining/DSC00236.JPG",
  "/mining/DSC00237.JPG", "/mining/DSC00238.JPG", "/mining/DSC00239.JPG", "/mining/DSC00240.JPG",
  "/mining/DSC00241.JPG", "/mining/DSC00242.JPG", "/mining/DSC00243.JPG", "/mining/DSC00244.JPG",
  "/mining/DSC00245.JPG", "/mining/DSC00246.JPG", "/mining/DSC00247.JPG", "/mining/DSC00248.JPG",
  "/mining/DSC00249.JPG", "/mining/DSC00250.JPG", "/mining/DSC00251.JPG", "/mining/DSC00252.JPG",
  "/mining/DSC00253.JPG", "/mining/DSC00254.JPG", "/mining/DSC00255.JPG", "/mining/DSC00256.JPG",
  "/mining/DSC00257.JPG", "/mining/DSC00258.JPG", "/mining/DSC00259.JPG", "/mining/DSC00260.JPG",
  "/mining/DSC00261.JPG", "/mining/DSC00262.JPG", "/mining/DSC00301.JPG",
  "/mining/DSC00263.JPG", "/mining/DSC00264.JPG", "/mining/DSC00265.JPG", "/mining/DSC00266.JPG",
  "/mining/DSC00267.JPG", "/mining/DSC00268.JPG", "/mining/DSC00269.JPG", "/mining/DSC00270.JPG",
  "/mining/DSC00271.JPG", "/mining/DSC00272.JPG", "/mining/DSC00273.JPG", "/mining/DSC00274.JPG",
  "/mining/DSC00275.JPG", "/mining/DSC00276.JPG", "/mining/DSC00277.JPG", "/mining/DSC00302.JPG",
  "/mining/DSC00278.JPG", "/mining/DSC00279.JPG", "/mining/DSC00280.JPG", "/mining/DSC00281.JPG",
  "/mining/DSC00282.JPG", "/mining/DSC00283.JPG", "/mining/DSC00284.JPG", "/mining/DSC00303.JPG",
  "/mining/DSC00285.JPG", "/mining/DSC00286.JPG", "/mining/DSC00287.JPG", "/mining/DSC00288.JPG",
  "/mining/DSC00289.JPG", "/mining/DSC00290.JPG", "/mining/DSC00291.JPG", "/mining/DSC00292.JPG",
  "/mining/DSC00293.JPG", "/mining/DSC00294.JPG", "/mining/DSC00295.JPG", "/mining/DSC00296.JPG",
  "/mining/DSC00297.JPG", "/mining/DSC00298.JPG", "/mining/DSC00299.JPG", "/mining/DSC00300.JPG",
];

const MiningGallery: React.FC<{ content?: any }> = ({ content }) => {
  const images = content?.images && content.images.length > 0 ? content.images : defaultImages;
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const prev = () => {
    setDirection(-1);
    setActiveIndex((p) => (p - 1 + images.length) % images.length);
  };

  const next = () => {
    setDirection(1);
    setActiveIndex((p) => (p + 1) % images.length);
  };

  const goToImage = (index: number) => {
    setDirection(index > activeIndex ? 1 : -1);
    setActiveIndex(index);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, images.length]);

  useEffect(() => {
    const container = document.getElementById("thumbnail-scroll");
    if (!container) return;

    const thumbWidth = 88;
    const containerWidth = container.clientWidth;
    const targetScroll = Math.max(
      0,
      activeIndex * thumbWidth - containerWidth / 2 + thumbWidth / 2
    );
    container.scrollTo({ left: targetScroll, behavior: "smooth" });
  }, [activeIndex]);

  const slideVariants = {
    enter: (direction: number) => ({ x: direction > 0 ? 240 : -240, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (direction: number) => ({ x: direction < 0 ? 240 : -240, opacity: 0 }),
  };

  const prevIndex = (activeIndex - 1 + images.length) % images.length;
  const nextIndex = (activeIndex + 1) % images.length;

  return (
    <section className="bg-white py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          viewport={{ once: true }}
          className="rounded-3xl border border-gray-200 bg-gray-50 p-8 shadow-sm"
        >
          <div className="text-xs uppercase tracking-widest text-gray-500">
            Hamdan Mining
          </div>
          <h2 className="mt-2 text-3xl font-bold text-gray-900">
            {content?.title || "Hamdan Mining Gallery"}
          </h2>
          <p className="mt-3 max-w-3xl text-sm text-gray-600 leading-relaxed">
            {content?.subtitle || "A visual overview of Hamdan Mining operations, production facilities, and site activities."}
          </p>
        </motion.div>

        {/* Main Slider */}
        <div className="relative">
          <div className="hidden lg:flex items-center justify-between gap-4 lg:gap-8">
            <motion.button
              onClick={prev}
              whileHover={{ scale: 1.06 }}
              whileTap={{ scale: 0.96 }}
              className="flex-shrink-0 w-12 h-12 rounded-full bg-red-600 text-white shadow-md hover:shadow-lg transition flex items-center justify-center"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-6 h-6" />
            </motion.button>

            <div className="flex-1 relative overflow-hidden rounded-3xl border shadow-[0_20px_60px_rgba(0,0,0,0.18)] bg-gray-900">
              <div className="flex items-center">
                <div className="hidden lg:block w-1/5 h-[420px] relative overflow-hidden flex-shrink-0">
                  <img
                    src={images[prevIndex]}
                    alt="Previous"
                    className="w-full h-full object-cover opacity-60"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-900/25 to-gray-900/85" />
                </div>

                <div className="flex-1 relative z-10">
                  <div className="aspect-video relative overflow-hidden">
                    <AnimatePresence mode="wait" custom={direction}>
                      <motion.img
                        key={activeIndex}
                        src={images[activeIndex]}
                        alt={`Mining image ${activeIndex + 1}`}
                        custom={direction}
                        variants={slideVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{
                          x: { type: "spring", stiffness: 220, damping: 26 },
                          opacity: { duration: 0.25 },
                        }}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).src =
                            "https://via.placeholder.com/1200x675?text=Image+Not+Found";
                        }}
                      />
                    </AnimatePresence>

                    <div className="absolute top-4 left-4 bg-white/85 backdrop-blur px-4 py-2 rounded-full text-xs font-semibold text-gray-900 border">
                      {activeIndex + 1} / {images.length}
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400" />
                  </div>
                </div>

                <div className="hidden lg:block w-1/5 h-[420px] relative overflow-hidden flex-shrink-0">
                  <img
                    src={images[nextIndex]}
                    alt="Next"
                    className="w-full h-full object-cover opacity-60"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-l from-transparent via-gray-900/25 to-gray-900/85" />
                </div>
              </div>
            </div>

            <motion.button
              onClick={next}
              whileHover={{ scale: 1.06 }}
              whileTap={{ scale: 0.96 }}
              className="flex-shrink-0 w-12 h-12 rounded-full bg-red-600 text-white shadow-md hover:shadow-lg transition flex items-center justify-center"
              aria-label="Next image"
            >
              <ChevronRight className="w-6 h-6" />
            </motion.button>
          </div>

          <div className="lg:hidden">
            <div className="relative overflow-hidden rounded-3xl border shadow-[0_20px_60px_rgba(0,0,0,0.18)] bg-gray-900">
              <div className="relative h-[62vh] min-h-[360px] max-h-[520px]">
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.img
                    key={activeIndex}
                    src={images[activeIndex]}
                    alt={`Mining image ${activeIndex + 1}`}
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{
                      x: { type: "spring", stiffness: 220, damping: 26 },
                      opacity: { duration: 0.25 },
                    }}
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).src =
                        "https://via.placeholder.com/1200x675?text=Image+Not+Found";
                    }}
                  />
                </AnimatePresence>

                <motion.button
                  onClick={prev}
                  whileTap={{ scale: 0.95 }}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/55 backdrop-blur text-white border border-white/15 flex items-center justify-center"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-6 h-6" />
                </motion.button>

                <motion.button
                  onClick={next}
                  whileTap={{ scale: 0.95 }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/55 backdrop-blur text-white border border-white/15 flex items-center justify-center"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-6 h-6" />
                </motion.button>

                <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-3 py-1.5 rounded-full text-xs font-semibold text-gray-900 border">
                  {activeIndex + 1} / {images.length}
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Thumbnails */}
        <div className="mt-8">
          <div
            id="thumbnail-scroll"
            className="flex gap-3 overflow-x-auto pb-2"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {images.map((src: string, index: number) => (
              <motion.button
                key={`${src}-${index}`}
                onClick={() => goToImage(index)}
                whileHover={{ scale: 1.05 }}
                className={`flex-shrink-0 rounded-xl overflow-hidden border transition-all duration-300 bg-white
                  ${index === activeIndex ? "border-red-600 shadow-md ring-2 ring-red-600/30" : "border-gray-200 opacity-70 hover:opacity-100"}
                  w-[76px] h-[76px] sm:w-[84px] sm:h-[84px]
                `}
                aria-label={`Go to image ${index + 1}`}
              >
                <img
                  src={src}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src =
                      "https://via.placeholder.com/200x200?text=No+Image";
                  }}
                />
              </motion.button>
            ))}
          </div>

          <style>{`
            #thumbnail-scroll::-webkit-scrollbar { display: none; }
          `}</style>
        </div>
      </div>
    </section>
  );
};

export default MiningGallery;
